import React, { useEffect, useMemo, useState } from 'react';
import {
  closeSupportTicket,
  createSupportTicket,
  getAssetUrl,
  getSupportTicket,
  getSupportTickets,
  replySupportTicket,
  submitSupportFeedback,
  uploadSupportAttachment,
} from '../services/api';
import SupportAttachmentPreview, { SupportAttachmentPreviewState } from '../components/SupportAttachmentPreview';
import SupportResolutionPanel from '../components/SupportResolutionPanel';
import {
  SupportTicket,
  SupportTicketCategory,
  SupportTicketMessage,
  SupportTicketPriority,
  SupportTicketStatus,
  SupportTicketThread,
} from '../types';

const CATEGORIES: Array<{ value: SupportTicketCategory; label: string; helper: string }> = [
  { value: 'device_data', label: 'Device / data issue', helper: 'Readings, gateways, dashboards, alerts' },
  { value: 'bug', label: 'Bug', helper: 'Something is not working as expected' },
  { value: 'billing', label: 'Billing / plan', helper: 'Plans, trial, invoices, upgrade requests' },
  { value: 'feature_request', label: 'Feature request', helper: 'A workflow or capability you want added' },
  { value: 'general', label: 'General inquiry', helper: 'Anything else the team should review' },
];

const PRIORITIES: Array<{ value: SupportTicketPriority; label: string; helper: string }> = [
  { value: 'low', label: 'Low', helper: 'Question or small request' },
  { value: 'normal', label: 'Normal', helper: 'Needs attention soon' },
  { value: 'high', label: 'High', helper: 'Affects operations' },
  { value: 'urgent', label: 'Urgent', helper: 'Critical blocker' },
];

const STATUS_COPY: Record<SupportTicketStatus, string> = {
  open: 'Open',
  in_progress: 'In progress',
  waiting_for_customer: 'Waiting for customer',
  resolved: 'Resolved',
  closed: 'Closed',
};

const CATEGORY_COPY: Record<SupportTicketCategory, string> = {
  bug: 'Bug',
  feature_request: 'Feature request',
  billing: 'Billing / plan',
  device_data: 'Device / data issue',
  general: 'General inquiry',
};

const ALLOWED_IMAGE_TYPES = ['image/png', 'image/jpeg', 'image/webp'];
const ALLOWED_IMAGE_EXTENSIONS = ['.png', '.jpg', '.jpeg', '.webp'];
const MAX_ATTACHMENT_SIZE = 5 * 1024 * 1024;

const INITIAL_FORM = {
  subject: '',
  category: 'device_data' as SupportTicketCategory,
  priority: 'normal' as SupportTicketPriority,
  message: '',
};

type SupportWorkflowView = 'new' | 'history' | 'feedback' | 'thread';

export default function SupportPage() {
  const [tickets, setTickets] = useState<SupportTicket[]>([]);
  const [selectedId, setSelectedId] = useState('');
  const [thread, setThread] = useState<SupportTicketThread | null>(null);
  const [loading, setLoading] = useState(true);
  const [threadLoading, setThreadLoading] = useState(false);
  const [form, setForm] = useState(INITIAL_FORM);
  const [createAttachments, setCreateAttachments] = useState<string[]>([]);
  const [replyAttachments, setReplyAttachments] = useState<string[]>([]);
  const [replyBody, setReplyBody] = useState('');
  const [uploadProgress, setUploadProgress] = useState(0);
  const [busy, setBusy] = useState('');
  const [notice, setNotice] = useState('');
  const [error, setError] = useState('');
  const [feedbackRating, setFeedbackRating] = useState(5);
  const [feedbackComment, setFeedbackComment] = useState('');
  const [preview, setPreview] = useState<SupportAttachmentPreviewState | null>(null);
  const [workflowView, setWorkflowView] = useState<SupportWorkflowView>('new');

  useEffect(() => {
    loadTickets();
  }, []);

  useEffect(() => {
    if (!selectedId) {
      setThread(null);
      return;
    }
    loadThread(selectedId);
  }, [selectedId]);

  const openTickets = useMemo(() => tickets.filter((ticket) => !['resolved', 'closed'].includes(ticket.status)).length, [tickets]);
  const waitingTickets = useMemo(() => tickets.filter((ticket) => ticket.status === 'waiting_for_customer').length, [tickets]);
  const resolvedTickets = useMemo(() => tickets.filter((ticket) => ['resolved', 'closed'].includes(ticket.status)).length, [tickets]);
  const activeTicketList = useMemo(() => tickets.filter((ticket) => ticket.status !== 'closed'), [tickets]);
  const archivedTicketList = useMemo(() => tickets.filter((ticket) => ticket.status === 'closed'), [tickets]);
  const selectedThread = thread?.ticket.id === selectedId ? thread : null;
  const selectedTicket = selectedThread?.ticket || tickets.find((ticket) => ticket.id === selectedId) || null;
  const displayTicket = selectedTicket;
  const latestTicket = tickets[0] || null;

  async function loadTickets(): Promise<SupportTicket[]> {
    setLoading(true);
    setError('');
    try {
      const next = await getSupportTickets();
      const firstActive = next.find((ticket) => ticket.status !== 'closed');
      setTickets(next);
      if (!selectedId && firstActive) {
        setSelectedId(firstActive.id);
      }
      if (selectedId && !next.some((ticket) => ticket.id === selectedId)) {
        setSelectedId(firstActive?.id || '');
      }
      return next;
    } catch (err: any) {
      setError(err.response?.data?.error || 'Unable to load support tickets.');
      return [];
    } finally {
      setLoading(false);
    }
  }

  async function loadThread(ticketId: string) {
    setThreadLoading(true);
    setThread(null);
    setError('');
    try {
      const next = await getSupportTicket(ticketId);
      setThread(next);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Unable to load ticket thread.');
    } finally {
      setThreadLoading(false);
    }
  }

  async function handleAttachmentUpload(event: React.ChangeEvent<HTMLInputElement>, target: 'create' | 'reply') {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!isAllowedSupportImage(file)) {
      setError('Upload a PNG, JPG, or WebP image under 5 MB.');
      event.target.value = '';
      return;
    }

    setBusy(`${target}-upload`);
    setUploadProgress(8);
    setError('');
    setNotice('');
    try {
      const result = await uploadSupportAttachment(file, setUploadProgress);
      if (target === 'create') {
        setCreateAttachments((current) => [...current, result.url]);
      } else {
        setReplyAttachments((current) => [...current, result.url]);
      }
      setNotice('Screenshot attached.');
    } catch (err: any) {
      setError(err.response?.data?.error || 'Unable to upload screenshot.');
    } finally {
      setBusy('');
      setUploadProgress(0);
      event.target.value = '';
    }
  }

  async function handleCreateTicket(event: React.FormEvent) {
    event.preventDefault();
    if (!form.subject.trim() || !form.message.trim()) {
      setError('Add a subject and message before opening a ticket.');
      return;
    }
    setBusy('create');
    setError('');
    setNotice('');
    try {
      const result = await createSupportTicket({
        ...form,
        subject: form.subject.trim(),
        message: form.message.trim(),
        attachments: createAttachments,
      });
      setForm(INITIAL_FORM);
      setCreateAttachments([]);
      setNotice('Support ticket opened. Seedronics support has been notified.');
      await loadTickets();
      setSelectedId(result.ticket.id);
      setWorkflowView('thread');
    } catch (err: any) {
      setError(err.response?.data?.error || 'Unable to open support ticket.');
    } finally {
      setBusy('');
    }
  }

  async function handleReply(event: React.FormEvent) {
    event.preventDefault();
    if (!selectedTicket || !replyBody.trim()) return;
    setBusy('reply');
    setError('');
    setNotice('');
    try {
      await replySupportTicket(selectedTicket.id, { body: replyBody.trim(), attachments: replyAttachments });
      setReplyBody('');
      setReplyAttachments([]);
      setNotice('Reply sent.');
      await Promise.all([loadTickets(), loadThread(selectedTicket.id)]);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Unable to send reply.');
    } finally {
      setBusy('');
    }
  }

  async function handleCloseTicket() {
    if (!selectedTicket || !window.confirm(`Close "${selectedTicket.subject}"?`)) return;
    setBusy('close');
    setError('');
    setNotice('');
    try {
      await closeSupportTicket(selectedTicket.id);
      setNotice('Ticket closed.');
      const next = await loadTickets();
      const nextActive = next.find((ticket) => ticket.id !== selectedTicket.id && ticket.status !== 'closed');
      setSelectedId(nextActive?.id || '');
      setThread(null);
      setWorkflowView('history');
    } catch (err: any) {
      setError(err.response?.data?.error || 'Unable to close ticket.');
    } finally {
      setBusy('');
    }
  }

  async function handleFeedback(event: React.FormEvent) {
    event.preventDefault();
    setBusy('feedback');
    setError('');
    setNotice('');
    try {
      await submitSupportFeedback({
        rating: feedbackRating,
        comment: feedbackComment.trim() || undefined,
        page: window.location.href,
      });
      setFeedbackComment('');
      setFeedbackRating(5);
      setNotice('Thanks. Your feedback was sent.');
    } catch (err: any) {
      setError(err.response?.data?.error || 'Unable to submit feedback.');
    } finally {
      setBusy('');
    }
  }

  function handlePreviewAttachment(url: string, title: string) {
    setPreview({
      alt: title,
      src: getAssetUrl(url) || url,
      title,
    });
  }

  function handleSelectTicket(ticketId: string) {
    setSelectedId(ticketId);
    setWorkflowView('thread');
  }

  return (
    <>
    <section className="sd-page-stack sd-support-page">
      <div className="sd-support-console-header">
        <div className="sd-support-hero-icon" aria-hidden="true">
          <SupportIcon icon="headset" />
        </div>
        <div className="sd-heading">
          <div className="sd-eyebrow">HELP & SUPPORT</div>
          <h1>Support Hub</h1>
          <p>Open a focused ticket, follow replies, or send a quick product signal without leaving the tenant workflow.</p>
        </div>
        <div className="sd-page-actions">
          <span className="sd-status-label is-online">{openTickets} active</span>
          <button className="sd-secondary-button" disabled={loading} onClick={loadTickets} type="button">
            <SupportIcon icon="refresh" />
            {loading ? 'Refreshing...' : 'Refresh'}
          </button>
        </div>
      </div>

      <div className="sd-support-status-strip" aria-label="Support summary">
        <SupportSummaryCard icon="flag" label="Open" value={openTickets.toLocaleString()} detail="Needs support attention" tone="blue" />
        <SupportSummaryCard icon="clock" label="Waiting" value={waitingTickets.toLocaleString()} detail="Needs tenant follow-up" tone="gold" />
        <SupportSummaryCard icon="check" label="Resolved" value={resolvedTickets.toLocaleString()} detail="Resolved or archived" tone="green" />
        <SupportSummaryCard icon="mail" label="Latest" value={latestTicket ? formatDate(latestTicket.updated_at) : 'None yet'} detail={latestTicket?.subject || 'No submitted tickets'} tone="muted" />
      </div>

      {(notice || error) && (
        <div className={`sd-inline-alert ${error ? 'is-danger' : 'is-success'}`}>
          {error || notice}
        </div>
      )}

      <div className="sd-support-workflow-tabs" role="tablist" aria-label="Support workflow">
        <button aria-selected={workflowView === 'new'} className={workflowView === 'new' ? 'is-active' : ''} onClick={() => setWorkflowView('new')} role="tab" type="button">
          New Ticket
        </button>
        <button aria-selected={workflowView === 'history' || workflowView === 'thread'} className={workflowView === 'history' || workflowView === 'thread' ? 'is-active' : ''} onClick={() => setWorkflowView('history')} role="tab" type="button">
          Check Ticket
        </button>
        <button aria-selected={workflowView === 'feedback'} className={workflowView === 'feedback' ? 'is-active' : ''} onClick={() => setWorkflowView('feedback')} role="tab" type="button">
          Feedback
        </button>
      </div>

      <div className={`sd-support-hub-layout is-view-${workflowView}`}>
        <div className="sd-support-left-column">
          <article className="sd-panel sd-support-card sd-support-create sd-support-mobile-pane is-new-pane">
            <div className="sd-support-card-header">
              <div>
                <h2>Open a Ticket</h2>
                <p>Share the site, device, dashboard, or alert involved so support can move quickly.</p>
              </div>
              <span className="sd-support-card-kicker">Step 1</span>
            </div>
            <form className="sd-support-form" onSubmit={handleCreateTicket}>
              <label className="sd-field">
                <span>Subject</span>
                <input value={form.subject} onChange={(event) => setForm((prev) => ({ ...prev, subject: event.target.value }))} placeholder="Fuel dashboard looks incorrect" />
              </label>
              <div className="sd-form-grid">
                <label className="sd-field">
                  <span>Category</span>
                  <select value={form.category} onChange={(event) => setForm((prev) => ({ ...prev, category: event.target.value as SupportTicketCategory }))}>
                    {CATEGORIES.map((category) => <option key={category.value} value={category.value}>{category.label}</option>)}
                  </select>
                  <small>{CATEGORIES.find((category) => category.value === form.category)?.helper}</small>
                </label>
                <div className="sd-field">
                  <span>Priority</span>
                  <PriorityPicker
                    value={form.priority}
                    onChange={(priority) => setForm((prev) => ({ ...prev, priority }))}
                  />
                </div>
              </div>
              <label className="sd-field">
                <span>Message</span>
                <textarea rows={6} value={form.message} onChange={(event) => setForm((prev) => ({ ...prev, message: event.target.value }))} placeholder="Describe what happened, what you expected, and the affected asset." />
              </label>
              <AttachmentPicker
                attachments={createAttachments}
                busy={busy === 'create-upload'}
                description="PNG, JPG, or WebP screenshots up to 5 MB"
                onChange={(event) => handleAttachmentUpload(event, 'create')}
                onRemove={(url) => setCreateAttachments((current) => current.filter((item) => item !== url))}
                progress={uploadProgress}
              />
              <button className="sd-primary-button sd-support-submit" disabled={busy === 'create'} type="submit">
                {busy === 'create' ? 'Opening Ticket...' : 'Open Ticket'}
              </button>
            </form>
          </article>

          <article className="sd-panel sd-support-card sd-support-feedback sd-support-mobile-pane is-feedback-pane">
            <div className="sd-support-card-header">
              <div>
                <h2>Quick Feedback</h2>
                <p>Send a short product signal without opening a ticket.</p>
              </div>
            </div>
            <form className="sd-support-form" onSubmit={handleFeedback}>
              <div className="sd-field">
                <span>Rating</span>
                <StarRating value={feedbackRating} onChange={setFeedbackRating} />
              </div>
              <label className="sd-field">
                <span>Comment</span>
                <textarea rows={3} value={feedbackComment} onChange={(event) => setFeedbackComment(event.target.value)} placeholder="What should we improve?" />
              </label>
              <button className="sd-secondary-button" disabled={busy === 'feedback'} type="submit">
                {busy === 'feedback' ? 'Sending...' : 'Send Feedback'}
              </button>
            </form>
          </article>
        </div>

        <div className="sd-support-right-column">
          <article className="sd-panel sd-support-card sd-support-list sd-support-mobile-pane is-history-pane">
            <div className="sd-support-card-header">
              <div>
                <h2>Ticket History</h2>
                <p>{activeTicketList.length} active and {archivedTicketList.length} archived ticket{tickets.length === 1 ? '' : 's'} in this workspace.</p>
              </div>
            </div>
            {loading ? (
              <SupportEmptyState title="Loading tickets..." detail="Checking your support history." />
            ) : tickets.length === 0 ? (
              <SupportEmptyState title="No support tickets yet" detail="When you open one, it will stay here so your team can check replies anytime." />
            ) : (
              <div className="sd-support-ticket-stack">
                {activeTicketList.length === 0 ? (
                  <SupportEmptyState title="No active tickets" detail="Closed tickets are tucked into the archive below." compact />
                ) : (
                  <TicketList tickets={activeTicketList} selectedId={selectedId} onSelect={handleSelectTicket} />
                )}
                {archivedTicketList.length > 0 && (
                  <details className="sd-support-archive">
                    <summary>Closed / Archive <span>{archivedTicketList.length}</span></summary>
                    <TicketList tickets={archivedTicketList} selectedId={selectedId} onSelect={handleSelectTicket} />
                  </details>
                )}
              </div>
            )}
          </article>

          <article className="sd-panel sd-support-card sd-support-thread sd-support-mobile-pane is-thread-pane">
            <button className="sd-support-thread-back" onClick={() => setWorkflowView('history')} type="button">
              Back to tickets
            </button>
            <div className="sd-support-thread-header">
              <div>
                <span className="sd-support-card-kicker">Conversation</span>
                <h2>{displayTicket ? displayTicket.subject : 'Ticket Thread'}</h2>
                <p>{displayTicket ? `${CATEGORY_COPY[displayTicket.category]} - ${STATUS_COPY[displayTicket.status]}` : 'Select a ticket to view replies.'}</p>
              </div>
              {displayTicket && (
                <div className="sd-support-thread-actions">
                  <PriorityBadge priority={displayTicket.priority} />
                  {displayTicket.status !== 'closed' && (
                    <button className="sd-secondary-button" disabled={busy === 'close'} onClick={handleCloseTicket} type="button">
                      {busy === 'close' ? 'Closing...' : 'Close Ticket'}
                    </button>
                  )}
                </div>
              )}
            </div>

            {threadLoading ? (
              <SupportEmptyState title="Loading thread..." detail="Fetching the full conversation." />
            ) : selectedThread ? (
              <>
                <div className="sd-support-message-list">
                  {selectedThread.messages.map((message) => <SupportMessage key={message.id} message={message} onPreview={handlePreviewAttachment} />)}
                </div>
                {selectedThread.resolution && <SupportResolutionPanel resolution={selectedThread.resolution} />}
                {selectedThread.ticket.status === 'closed' ? (
                  <div className="sd-inline-alert">This archived ticket is closed. Open a new ticket if the issue returns.</div>
                ) : (
                  <form className="sd-support-reply" onSubmit={handleReply}>
                    <label className="sd-field">
                      <span>Reply</span>
                      <textarea rows={4} value={replyBody} onChange={(event) => setReplyBody(event.target.value)} placeholder="Add an update for Seedronics support." />
                    </label>
                    <AttachmentPicker
                      attachments={replyAttachments}
                      busy={busy === 'reply-upload'}
                      compact
                      description="Attach a new screenshot if it helps explain the update."
                      onChange={(event) => handleAttachmentUpload(event, 'reply')}
                      onRemove={(url) => setReplyAttachments((current) => current.filter((item) => item !== url))}
                      progress={uploadProgress}
                    />
                    <button className="sd-primary-button" disabled={busy === 'reply' || !replyBody.trim()} type="submit">
                      {busy === 'reply' ? 'Sending...' : 'Send Reply'}
                    </button>
                  </form>
                )}
              </>
            ) : (
              <SupportEmptyState title="Select a ticket" detail="Submitted tickets remain available here, including replies from Seedronics support." />
            )}
          </article>
        </div>
      </div>
    </section>
    <SupportAttachmentPreview preview={preview} onClose={() => setPreview(null)} />
    </>
  );
}

function TicketList({
  onSelect,
  selectedId,
  tickets,
}: {
  onSelect: (id: string) => void;
  selectedId: string;
  tickets: SupportTicket[];
}) {
  return (
    <div className="sd-support-ticket-list">
      {tickets.map((ticket) => (
        <button
          className={ticket.id === selectedId ? 'sd-ticket-card is-active' : 'sd-ticket-card'}
          key={ticket.id}
          onClick={() => onSelect(ticket.id)}
          type="button"
        >
          <span className="sd-ticket-card-topline">
            <span className="sd-ticket-card-number">{getTicketNumber(ticket)}</span>
            <span className={`sd-support-status is-${ticket.status}`}>{STATUS_COPY[ticket.status]}</span>
            <PriorityBadge priority={ticket.priority} />
          </span>
          <strong>{ticket.subject}</strong>
          <span className="sd-ticket-card-meta">
            <span>{CATEGORY_COPY[ticket.category]}</span>
            <span>{ticket.message_count || 0} message{ticket.message_count === 1 ? '' : 's'}</span>
            <span>{formatDate(ticket.last_message_at || ticket.updated_at)}</span>
          </span>
        </button>
      ))}
    </div>
  );
}

function PriorityPicker({ onChange, value }: { onChange: (priority: SupportTicketPriority) => void; value: SupportTicketPriority }) {
  return (
    <div className="sd-priority-picker" role="radiogroup" aria-label="Ticket priority">
      {PRIORITIES.map((priority) => (
        <button
          aria-checked={value === priority.value}
          className={value === priority.value ? `is-active is-${priority.value}` : `is-${priority.value}`}
          key={priority.value}
          onClick={() => onChange(priority.value)}
          role="radio"
          type="button"
        >
          <PriorityBadge priority={priority.value} />
          <small>{priority.helper}</small>
        </button>
      ))}
    </div>
  );
}

function PriorityBadge({ priority }: { priority: SupportTicketPriority }) {
  const copy = PRIORITIES.find((item) => item.value === priority)?.label || priority;
  return (
    <span className={`sd-priority-badge is-${priority}`}>
      <SupportIcon icon="flag" />
      {copy}
    </span>
  );
}

function AttachmentPicker({
  attachments,
  busy,
  compact = false,
  description,
  onChange,
  onRemove,
  progress,
}: {
  attachments: string[];
  busy: boolean;
  compact?: boolean;
  description: string;
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onRemove: (url: string) => void;
  progress: number;
}) {
  return (
    <div className={compact ? 'sd-support-attachments is-compact' : 'sd-support-attachments'}>
      <label>
        <span className="sd-support-upload-icon" aria-hidden="true">
          <SupportIcon icon="image" />
        </span>
        <span>
          <strong>{busy ? `Uploading ${progress || 8}%` : 'Attach Screenshot'}</strong>
          <small>{description}</small>
        </span>
        <input accept="image/png,image/jpeg,image/webp" disabled={busy} onChange={onChange} type="file" />
      </label>
      {busy && (
        <div className="sd-logo-progress-track" aria-hidden="true">
          <div className="sd-logo-progress-bar" style={{ width: `${Math.max(8, progress)}%` }} />
        </div>
      )}
      {attachments.length > 0 && (
        <div className="sd-support-attachment-list">
          {attachments.map((url) => {
            const assetUrl = getAssetUrl(url) || url;
            return (
              <div className="sd-support-attachment-chip" key={url}>
                <img src={assetUrl} alt="Uploaded support screenshot" />
                <span>{url.split('/').pop()}</span>
                <button aria-label="Remove screenshot" onClick={() => onRemove(url)} type="button">Remove</button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function StarRating({ onChange, value }: { onChange: (rating: number) => void; value: number }) {
  return (
    <div className="sd-star-rating" role="radiogroup" aria-label="Feedback rating">
      {[1, 2, 3, 4, 5].map((rating) => (
        <button
          aria-checked={value === rating}
          aria-label={`${rating} star${rating === 1 ? '' : 's'}`}
          className={rating <= value ? 'is-active' : ''}
          key={rating}
          onClick={() => onChange(rating)}
          role="radio"
          type="button"
        >
          <SupportIcon icon="star" filled={rating <= value} />
        </button>
      ))}
      <span>{value} / 5</span>
    </div>
  );
}

function SupportSummaryCard({
  detail,
  icon,
  label,
  tone,
  value,
}: {
  detail: string;
  icon: SupportIconName;
  label: string;
  tone: 'blue' | 'gold' | 'green' | 'muted';
  value: string;
}) {
  return (
    <article className={`sd-support-summary-card is-${tone}`}>
      <span aria-hidden="true">
        <SupportIcon icon={icon} />
      </span>
      <div>
        <small>{label}</small>
        <strong>{value}</strong>
        <p>{detail}</p>
      </div>
    </article>
  );
}

function SupportEmptyState({ compact = false, detail, title }: { compact?: boolean; detail: string; title: string }) {
  return (
    <div className={compact ? 'sd-support-empty is-compact' : 'sd-support-empty'}>
      <span aria-hidden="true">
        <SupportIcon icon="headset" />
      </span>
      <strong>{title}</strong>
      <p>{detail}</p>
    </div>
  );
}

function SupportMessage({ message, onPreview }: { message: SupportTicketMessage; onPreview: (url: string, title: string) => void }) {
  const author = message.is_superadmin
    ? 'Seedronics Support'
    : message.author_name || message.author_email || 'Workspace user';
  return (
    <div className={`sd-support-message ${message.is_superadmin ? 'is-support' : ''}`}>
      <div>
        <strong>{author}</strong>
        <span>{formatDate(message.created_at)}</span>
      </div>
      <p>{message.body}</p>
      {!!message.attachments?.length && (
        <div className="sd-support-message-attachments">
          {message.attachments.map((url) => {
            const assetUrl = getAssetUrl(url) || url;
            const title = url.split('/').pop() || 'Support attachment';
            return (
              <button key={url} onClick={() => onPreview(url, title)} type="button">
                <img src={assetUrl} alt="Support attachment" />
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

type SupportIconName = 'check' | 'clock' | 'flag' | 'headset' | 'image' | 'mail' | 'refresh' | 'star';

function SupportIcon({ filled = false, icon }: { filled?: boolean; icon: SupportIconName }) {
  const common = {
    fill: filled ? 'currentColor' : 'none',
    stroke: 'currentColor',
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
    strokeWidth: 2,
  };

  return (
    <svg viewBox="0 0 24 24" focusable="false">
      {icon === 'headset' && (
        <>
          <path {...common} d="M4 13a8 8 0 0 1 16 0" />
          <path {...common} d="M18 19c0 1.1-.9 2-2 2h-3" />
          <rect {...common} x="2.8" y="12" width="4" height="6" rx="2" />
          <rect {...common} x="17.2" y="12" width="4" height="6" rx="2" />
          <path {...common} d="M13 21h-2" />
        </>
      )}
      {icon === 'image' && (
        <>
          <rect {...common} x="4" y="5" width="16" height="14" rx="2" />
          <circle {...common} cx="9" cy="10" r="1.5" />
          <path {...common} d="M20 16l-4-4-4 4-2-2-4 4" />
        </>
      )}
      {icon === 'flag' && (
        <>
          <path {...common} d="M5 21V5" />
          <path {...common} d="M5 5h11l-1.5 4L16 13H5" />
        </>
      )}
      {icon === 'star' && <path {...common} d="M12 3.5l2.7 5.5 6.1.9-4.4 4.3 1 6.1L12 17.4l-5.4 2.9 1-6.1-4.4-4.3 6.1-.9L12 3.5z" />}
      {icon === 'refresh' && (
        <>
          <path {...common} d="M20 11a8 8 0 0 0-14.2-4.8L4 8" />
          <path {...common} d="M4 4v4h4" />
          <path {...common} d="M4 13a8 8 0 0 0 14.2 4.8L20 16" />
          <path {...common} d="M20 20v-4h-4" />
        </>
      )}
      {icon === 'mail' && (
        <>
          <rect {...common} x="3" y="5" width="18" height="14" rx="2" />
          <path {...common} d="M4 7l8 6 8-6" />
        </>
      )}
      {icon === 'clock' && (
        <>
          <circle {...common} cx="12" cy="12" r="8" />
          <path {...common} d="M12 8v5l3 2" />
        </>
      )}
      {icon === 'check' && (
        <>
          <circle {...common} cx="12" cy="12" r="8" />
          <path {...common} d="M8.5 12.5l2.2 2.2 4.8-5.2" />
        </>
      )}
    </svg>
  );
}

function isAllowedSupportImage(file: File) {
  const lowerName = file.name.toLowerCase();
  const hasAllowedExtension = ALLOWED_IMAGE_EXTENSIONS.some((extension) => lowerName.endsWith(extension));
  return ALLOWED_IMAGE_TYPES.includes(file.type) && hasAllowedExtension && file.size <= MAX_ATTACHMENT_SIZE;
}

function getTicketNumber(ticket: Pick<SupportTicket, 'created_at' | 'id' | 'ticket_number'>) {
  if (ticket.ticket_number) return ticket.ticket_number;
  const year = ticket.created_at ? new Date(ticket.created_at).getFullYear() : new Date().getFullYear();
  return `SUP-${year}-${ticket.id.slice(0, 8).toUpperCase()}`;
}

function formatDate(value?: string | null) {
  if (!value) return '-';
  return new Date(value).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}
